import { renderTemplate, type TemplateKey, type TemplateVars } from '@core/email/templates';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { supabase } from '../client';
import { isPostgrestRelationMissing, isUndefinedTableError } from '../postgrestErrors';

export type SchedulingProvider = 'manual' | 'luma' | 'calendly';

/** DB row uses `completed`; app surfaces `conducted`. */
type InterviewEventRowStatus =
  | 'draft'
  | 'awaiting_booking'
  | 'scheduled'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export type InterviewEventStatus =
  | 'draft'
  | 'awaiting_booking'
  | 'scheduled'
  | 'conducted'
  | 'cancelled'
  | 'no_show';

export type InterviewAttendee = {
  name: string;
  email?: string;
  role?: string;
};

export type InterviewSchedulingMeta = {
  candidateAvailability?: string;
  panelAvailability?: string;
  [key: string]: unknown;
};

export interface InterviewEvent {
  id: string;
  org_id: string;
  application_id: string;
  round: number;
  /** Null while in draft / awaiting self-booking (§9.8). */
  scheduled_at: string | null;
  duration_minutes: number | null;
  location: string | null;
  interviewer_name: string | null;
  interviewer_email: string | null;
  notes: string | null;
  scheduling_provider: SchedulingProvider | null;
  scheduling_booking_url: string | null;
  invite_sent_at: string | null;
  /** DB `attendees` JSONB — panelists beyond lead interviewer. */
  attendees: InterviewAttendee[];
  /** DB `scheduling_meta` JSONB — availability notes, wizard context (migration 048). */
  scheduling_meta: InterviewSchedulingMeta;
  status: InterviewEventStatus;
  created_at: string;
  updated_at: string;
}

export type InterviewEventWithApplication = InterviewEvent & {
  application: {
    id: string;
    status?: string;
    candidate: { id: string; full_name: string; email?: string | null } | null;
    job: { id: string; title: string } | null;
    client: { id: string; name: string } | null;
  } | null;
};

/** Direct schedule path: concrete `scheduled_at` required (legacy + application detail form). */
export type InterviewEventCreate = {
  org_id: string;
  application_id: string;
  round: number;
  scheduled_at: string;
} & Partial<
  Pick<
    InterviewEvent,
    | 'location'
    | 'interviewer_name'
    | 'interviewer_email'
    | 'notes'
    | 'duration_minutes'
    | 'attendees'
    | 'scheduling_meta'
  >
>;

export type InterviewLifecycleCreate = Pick<InterviewEvent, 'org_id' | 'application_id' | 'round'> &
  Partial<
    Pick<
      InterviewEvent,
      | 'scheduling_provider'
      | 'scheduling_booking_url'
      | 'notes'
      | 'interviewer_name'
      | 'interviewer_email'
      | 'attendees'
      | 'scheduling_meta'
    >
  >;

export type ScheduledInterviewWithApp = {
  id: string;
  application_id: string;
  scheduled_at: string;
  round: number;
  duration_minutes: number | null;
  status: string;
  application: {
    candidate: { full_name: string } | null;
    job: { title: string } | null;
    client: { name: string } | null;
  } | null;
};

function mapStatusFromDb(raw: string): InterviewEventStatus {
  if (raw === 'completed') return 'conducted';
  return raw as InterviewEventStatus;
}

function mapStatusToDb(status: InterviewEventStatus): InterviewEventRowStatus {
  if (status === 'conducted') return 'completed';
  return status as InterviewEventRowStatus;
}

/** DB uses `location_or_link` and status `completed` instead of `conducted`. */
function mapInterviewEventRow(row: Record<string, unknown>): InterviewEvent {
  const statusRaw = String(row.status ?? 'scheduled');
  // attendees JSONB may store lead interviewer email when the direct column is absent
  const attendeesRaw = Array.isArray(row.attendees) ? (row.attendees as InterviewAttendee[]) : [];
  const leadAttendee = attendeesRaw.find((a) => a.role === 'interviewer') ?? attendeesRaw[0] ?? null;
  return {
    id: String(row.id),
    org_id: String(row.org_id),
    application_id: String(row.application_id),
    round: Number(row.round),
    scheduled_at:
      row.scheduled_at == null || row.scheduled_at === ''
        ? null
        : String(row.scheduled_at),
    duration_minutes:
      row.duration_minutes != null && row.duration_minutes !== ''
        ? Number(row.duration_minutes)
        : null,
    location:
      (row.location_or_link as string | null | undefined) ??
      (row.location as string | null | undefined) ??
      null,
    interviewer_name:
      (row.interviewer_name as string | null | undefined) ??
      leadAttendee?.name ?? null,
    interviewer_email:
      (row.interviewer_email as string | null | undefined) ??
      leadAttendee?.email ?? null,
    notes: (row.notes as string | null | undefined) ?? null,
    scheduling_provider: (row.scheduling_provider as SchedulingProvider | null | undefined) ?? null,
    scheduling_booking_url: (row.scheduling_booking_url as string | null | undefined) ?? null,
    invite_sent_at:
      row.invite_sent_at == null || row.invite_sent_at === ''
        ? null
        : String(row.invite_sent_at),
    attendees: attendeesRaw,
    scheduling_meta: (row.scheduling_meta as InterviewSchedulingMeta | null | undefined) ?? {},
    status: mapStatusFromDb(statusRaw),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  };
}

function toDbInsertBody(payload: InterviewEventCreate): Record<string, unknown> {
  const body: Record<string, unknown> = {
    org_id: payload.org_id,
    application_id: payload.application_id,
    round: payload.round,
    scheduled_at: payload.scheduled_at,
    duration_minutes: payload.duration_minutes ?? 60,
    location_or_link: payload.location ?? null,
    // interviewer_name / interviewer_email columns may not exist — stored in attendees JSONB below
    notes: payload.notes ?? null,
    status: 'scheduled',
  };

  // Build attendees: lead interviewer first, then any additional panelists
  const attendees: InterviewAttendee[] = [];
  if (payload.interviewer_name?.trim() || payload.interviewer_email?.trim()) {
    attendees.push({
      name: payload.interviewer_name?.trim() ?? '',
      email: payload.interviewer_email?.trim() || undefined,
      role: 'interviewer',
    });
  }
  for (const a of payload.attendees ?? []) {
    if (a.name?.trim()) attendees.push(a);
  }
  body.attendees = attendees;

  const meta = payload.scheduling_meta;
  if (meta && typeof meta === 'object' && !Array.isArray(meta) && Object.keys(meta).length > 0) {
    body.scheduling_meta = meta;
  }
  return body;
}

export type InterviewEventUpdatePatch = Partial<
  Pick<
    InterviewEvent,
    | 'status'
    | 'notes'
    | 'location'
    | 'scheduled_at'
    | 'duration_minutes'
    | 'scheduling_provider'
    | 'scheduling_booking_url'
    | 'invite_sent_at'
    | 'interviewer_name'
    | 'interviewer_email'
    | 'attendees'
    | 'scheduling_meta'
  >
>;

function toDbUpdateBody(patch: InterviewEventUpdatePatch): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (patch.notes !== undefined) db.notes = patch.notes;
  if (patch.scheduled_at !== undefined) db.scheduled_at = patch.scheduled_at;
  if (patch.duration_minutes !== undefined) db.duration_minutes = patch.duration_minutes;
  if (patch.location !== undefined) db.location_or_link = patch.location;
  // interviewer_name / interviewer_email columns may not exist in DB — merge into attendees JSONB
  if (patch.interviewer_name !== undefined || patch.interviewer_email !== undefined) {
    const existing: InterviewAttendee[] = Array.isArray(patch.attendees) ? [...patch.attendees] : [];
    const leadIdx = existing.findIndex((a) => a.role === 'interviewer');
    if (leadIdx >= 0) {
      if (patch.interviewer_name !== undefined) existing[leadIdx].name = patch.interviewer_name ?? '';
      if (patch.interviewer_email !== undefined) existing[leadIdx].email = patch.interviewer_email ?? undefined;
    } else {
      existing.unshift({
        name: patch.interviewer_name?.trim() ?? '',
        email: patch.interviewer_email?.trim() || undefined,
        role: 'interviewer',
      });
    }
    db.attendees = existing;
  } else if (patch.attendees !== undefined) {
    db.attendees = patch.attendees;
  }
  if (patch.scheduling_provider !== undefined) db.scheduling_provider = patch.scheduling_provider;
  if (patch.scheduling_booking_url !== undefined) db.scheduling_booking_url = patch.scheduling_booking_url;
  if (patch.invite_sent_at !== undefined) db.invite_sent_at = patch.invite_sent_at;
  if (patch.scheduling_meta !== undefined) db.scheduling_meta = patch.scheduling_meta;
  if (patch.status !== undefined) {
    db.status = mapStatusToDb(patch.status);
  }
  return db;
}

function buildInterviewConfirmationMergeVars(opts: {
  scheduled_at: string;
  round: number;
  location?: string | null;
  candidateName?: string;
  jobTitle?: string;
  clientName?: string;
  recruiterName?: string;
  orgName?: string;
}): Record<string, string> {
  return {
    CANDIDATE_NAME: opts.candidateName ?? 'Candidate',
    JOB_TITLE: opts.jobTitle ?? 'Role',
    CLIENT_NAME: opts.clientName ?? 'Client',
    RECRUITER_NAME: opts.recruiterName ?? 'Recruiter',
    ORG_NAME: opts.orgName ?? 'GoGrabJob',
    INTERVIEW_DATE: formatDateTimeGmt(opts.scheduled_at),
    INTERVIEW_LOCATION: opts.location?.trim() || 'To be confirmed',
    INTERVIEW_ROUND: String(opts.round),
  };
}

function embedOne<T extends object>(v: T | T[] | null | undefined): T | null {
  if (v == null) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

async function fetchApplicationCandidateEmail(applicationId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('ggj_b2b_applications')
    .select('candidate:ggj_b2b_candidates(email)')
    .eq('id', applicationId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as { candidate?: { email?: string | null } | { email?: string | null }[] | null };
  const c = embedOne(row.candidate as object | object[] | null | undefined) as { email?: string | null } | null;
  const em = c?.email?.trim();
  return em || null;
}

export const interviewEventService = {
  /**
   * Interview hub: all events for an org with application → candidate, job, client (newest first).
   */
  async listForOrgWithApplications(orgId: string, opts?: { limit?: number }): Promise<InterviewEventWithApplication[]> {
    const limit = opts?.limit ?? 400;
    const { data, error } = await supabase
      .from('ggj_b2b_interview_events')
      .select(
        `
        *,
        application:ggj_b2b_applications(
          id,
          status,
          candidate:ggj_b2b_candidates(id, full_name, email),
          job:ggj_b2b_jobs(id, title),
          client:ggj_b2b_clients(id, name)
        )
      `
      )
      .eq('org_id', orgId)
      .order('updated_at', { ascending: false })
      .limit(limit);
    if (error) {
      if (isPostgrestRelationMissing(error)) return [];
      throw error;
    }
    return (data ?? []).map((raw) => {
      const row = raw as Record<string, unknown>;
      const ev = mapInterviewEventRow(row);
      const appRaw = embedOne(row.application as object | object[] | null | undefined);
      const appRec = appRaw as Record<string, unknown> | null;
      let application: InterviewEventWithApplication['application'] = null;
      if (appRec && typeof appRec === 'object') {
        const cand = embedOne(appRec.candidate as object | object[] | null | undefined) as Record<string, unknown> | null;
        const job = embedOne(appRec.job as object | object[] | null | undefined) as Record<string, unknown> | null;
        const client = embedOne(appRec.client as object | object[] | null | undefined) as Record<string, unknown> | null;
        application = {
          id: String(appRec.id),
          status: typeof appRec.status === 'string' ? appRec.status : undefined,
          candidate: cand
            ? {
                id: String(cand.id),
                full_name: String(cand.full_name ?? ''),
                email: (cand.email as string | null | undefined) ?? null,
              }
            : null,
          job: job ? { id: String(job.id), title: String(job.title ?? '') } : null,
          client: client ? { id: String(client.id), name: String(client.name ?? '') } : null,
        };
      }
      return { ...ev, application };
    });
  },

  async listByApplication(applicationId: string): Promise<InterviewEvent[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_events')
      .select('*')
      .eq('application_id', applicationId)
      .order('round', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) {
      if (isPostgrestRelationMissing(error)) return [];
      throw error;
    }
    return (data ?? []).map((r) => mapInterviewEventRow(r as Record<string, unknown>));
  },

  async listToday(orgId: string): Promise<InterviewEvent[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today.getTime() + 86400000);
    const { data, error } = await supabase
      .from('ggj_b2b_interview_events')
      .select('*')
      .eq('org_id', orgId)
      .gte('scheduled_at', today.toISOString())
      .lt('scheduled_at', tomorrow.toISOString())
      .order('scheduled_at');
    if (error) {
      if (isPostgrestRelationMissing(error)) return [];
      throw error;
    }
    return (data ?? []).map((r) => mapInterviewEventRow(r as Record<string, unknown>));
  },

  /** SCH-020: scheduled interviews in a window for scheduler / ICS */
  async listScheduledInRange(orgId: string, rangeStart: Date, rangeEnd: Date): Promise<ScheduledInterviewWithApp[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_events')
      .select(
        `
        id, application_id, scheduled_at, round, duration_minutes, status,
        application:ggj_b2b_applications(
          candidate:ggj_b2b_candidates(full_name),
          job:ggj_b2b_jobs(title),
          client:ggj_b2b_clients(name)
        )
      `
      )
      .eq('org_id', orgId)
      .gte('scheduled_at', rangeStart.toISOString())
      .lte('scheduled_at', rangeEnd.toISOString())
      .order('scheduled_at');
    if (error) {
      if (isPostgrestRelationMissing(error)) return [];
      throw error;
    }
    return (data ?? []) as ScheduledInterviewWithApp[];
  },

  /**
   * §9.8: Draft or awaiting_booking row (no slot yet). Paste Luma/Calendly (or manual) booking URL optional.
   */
  async createLifecycleEntry(payload: InterviewLifecycleCreate): Promise<InterviewEvent> {
    const url = payload.scheduling_booking_url?.trim() || null;
    const status: InterviewEventRowStatus = url ? 'awaiting_booking' : 'draft';
    const meta = payload.scheduling_meta ?? {};
    // Build attendees: lead interviewer first, then additional panelists
    const attendees: InterviewAttendee[] = [];
    if (payload.interviewer_name?.trim() || payload.interviewer_email?.trim()) {
      attendees.push({
        name: payload.interviewer_name?.trim() ?? '',
        email: payload.interviewer_email?.trim() || undefined,
        role: 'interviewer',
      });
    }
    for (const a of payload.attendees ?? []) {
      if (a.name?.trim()) attendees.push(a);
    }
    const insertBody: Record<string, unknown> = {
      org_id: payload.org_id,
      application_id: payload.application_id,
      round: payload.round,
      scheduled_at: null,
      location_or_link: null,
      // interviewer_name / interviewer_email not direct columns — stored in attendees JSONB
      notes: payload.notes ?? null,
      status,
      scheduling_provider: payload.scheduling_provider ?? null,
      scheduling_booking_url: url,
      attendees,
    };
    if (meta && typeof meta === 'object' && !Array.isArray(meta) && Object.keys(meta).length > 0) {
      insertBody.scheduling_meta = meta;
    }
    const { data, error } = await supabase
      .from('ggj_b2b_interview_events')
      .insert(insertBody)
      .select()
      .single();
    if (error) throw error;
    return mapInterviewEventRow(data as Record<string, unknown>);
  },

  /**
   * §9.13: Queue transactional rows on `ggj_b2b_email_send_queue` (EML-021 shape).
   * Candidate → `interview_candidate_invite`; lead + panel emails → `interview_panel_calendar_hold`.
   * Requires a worker/cron to drain `pending` rows (same as requisition queue).
   */
  async enqueueInterviewConfirmationEmail(opts: {
    org_id: string;
    application_id: string;
    scheduled_at: string;
    round: number;
    location?: string | null;
    candidateName?: string;
    jobTitle?: string;
    clientName?: string;
    recruiterName?: string;
    orgName?: string;
    /** When omitted, loaded from `ggj_b2b_applications` → candidate */
    candidateEmail?: string | null;
    interviewerEmail?: string | null;
    interviewerName?: string | null;
    panelRecipients?: { email: string; name?: string }[];
    createdBy?: string | null;
  }): Promise<void> {
    const { data: orgRow } = await supabase
      .from('ggj_b2b_organizations')
      .select('billing_email')
      .eq('id', opts.org_id)
      .maybeSingle();
    const fromAddress =
      ((orgRow as { billing_email?: string | null } | null)?.billing_email ?? '').trim() ||
      'noreply@gograbjob.com';

    const mergeFlat = buildInterviewConfirmationMergeVars(opts);
    const refId = opts.application_id.replace(/-/g, '').slice(0, 10);
    const baseVars: TemplateVars = {
      ...mergeFlat,
      REF_ID: refId,
      TRACK_PIXEL: '',
    };

    const now = new Date().toISOString();
    const insertQueueRow = async (templateKey: TemplateKey, toEmail: string, vars: TemplateVars) => {
      const { subject, bodyHtml } = renderTemplate(templateKey, vars);
      const { error } = await supabase.from('ggj_b2b_email_send_queue').insert({
        org_id: opts.org_id,
        application_id: opts.application_id,
        template_key: templateKey,
        from_address: fromAddress,
        to_addresses: [toEmail.trim()],
        cc_addresses: [],
        subject: subject.slice(0, 500),
        body_html: bodyHtml,
        merge_vars: { ...mergeFlat, template_key: templateKey },
        status: 'pending',
        // scheduled_at must be set so drainEmailQueue (.lte('scheduled_at', now)) picks it up
        scheduled_at: now,
        created_by: opts.createdBy ?? opts.recruiterName ?? null,
      });
      if (error) throw error;
    };

    const usedEmails = new Set<string>();

    let candidateEmail = opts.candidateEmail?.trim() || null;
    if (!candidateEmail) {
      candidateEmail = await fetchApplicationCandidateEmail(opts.application_id);
    }
    if (candidateEmail) {
      const norm = candidateEmail.toLowerCase();
      usedEmails.add(norm);
      await insertQueueRow('interview_candidate_invite', candidateEmail, baseVars);
    }

    const panelTargets: { email: string; name?: string }[] = [];
    if (opts.interviewerEmail?.trim()) {
      panelTargets.push({
        email: opts.interviewerEmail.trim(),
        name: opts.interviewerName?.trim() || undefined,
      });
    }
    for (const p of opts.panelRecipients ?? []) {
      const e = p.email?.trim();
      if (e) panelTargets.push({ email: e, name: p.name?.trim() || undefined });
    }
    const seenPanel = new Set<string>();
    for (const p of panelTargets) {
      const norm = p.email.toLowerCase();
      if (seenPanel.has(norm) || usedEmails.has(norm)) continue;
      seenPanel.add(norm);
      usedEmails.add(norm);
      const panelVars: TemplateVars = {
        ...baseVars,
        PANELIST_NAME: p.name || 'There',
      };
      await insertQueueRow('interview_panel_calendar_hold', p.email, panelVars);
    }

    // Immediately drain the queue so emails are sent without waiting for the cron scheduler.
    // Swallow any edge-function invocation error — queue rows remain and will be picked up on next cron tick.
    try {
      await supabase.functions.invoke('ggj_b2b_run-schedulers', { method: 'POST', body: {} });
    } catch (schedErr) {
      console.warn('[interviewEventService] Could not invoke scheduler to drain email queue:', schedErr);
    }
  },

  /**
   * EML-060: create event + optionally enqueue notification email.
   *
   * §9.13: INSERT with status=scheduled fires an outbox trigger (`interview.scheduled`).
   * When `ggj_b2b_outbox_events` is not yet deployed the trigger rolls back the whole INSERT.
   * Strategy: try as 'scheduled'; on 42P01/PGRST205 retry as 'draft' (no trigger), then
   * patch status to 'scheduled' in a separate UPDATE (swallowing any further trigger error).
   */
  async create(
    payload: InterviewEventCreate,
    opts?: {
      notifyCandidate?: boolean;
      candidateName?: string;
      candidateEmail?: string | null;
      jobTitle?: string;
      clientName?: string;
      recruiterName?: string;
      orgName?: string;
      createdBy?: string | null;
    }
  ): Promise<InterviewEvent> {
    const insertBody = toDbInsertBody(payload); // status = 'scheduled'

    let rowData: Record<string, unknown>;

    const first = await supabase
      .from('ggj_b2b_interview_events')
      .insert(insertBody)
      .select()
      .single();

    if (first.error) {
      if (isUndefinedTableError(first.error)) {
        // Outbox trigger rolled back the INSERT — retry without triggering it
        console.warn('[interviewEventService] Outbox trigger missing, retrying as draft:', first.error.message);
        const retry = await supabase
          .from('ggj_b2b_interview_events')
          .insert({ ...insertBody, status: 'draft' })
          .select()
          .single();
        if (retry.error) throw retry.error;
        rowData = retry.data as Record<string, unknown>;

        // Patch status to 'scheduled' in a separate query; swallow trigger error if it recurs
        const patch = await supabase
          .from('ggj_b2b_interview_events')
          .update({ status: 'scheduled' })
          .eq('id', (rowData as { id: string }).id)
          .select()
          .single();
        if (!patch.error && patch.data) {
          rowData = patch.data as Record<string, unknown>;
        } else {
          // Outbox trigger also blocks UPDATE — mark row as scheduled in-memory only
          rowData = { ...rowData, status: 'scheduled' };
          console.warn('[interviewEventService] Could not patch status to scheduled, outbox trigger blocks UPDATE too.');
        }
      } else {
        throw first.error;
      }
    } else {
      rowData = first.data as Record<string, unknown>;
    }

    const event = mapInterviewEventRow(rowData);

    if (opts?.notifyCandidate !== false) {
      // Email queue may also have an outbox trigger — swallow gracefully
      try {
        await interviewEventService.enqueueInterviewConfirmationEmail({
          org_id: payload.org_id,
          application_id: payload.application_id,
          scheduled_at: payload.scheduled_at,
          round: payload.round,
          location: payload.location,
          candidateName: opts?.candidateName,
          candidateEmail: opts?.candidateEmail ?? null,
          jobTitle: opts?.jobTitle,
          clientName: opts?.clientName,
          recruiterName: opts?.recruiterName,
          orgName: opts?.orgName,
          interviewerEmail: payload.interviewer_email,
          interviewerName: payload.interviewer_name,
          panelRecipients: (payload.attendees ?? [])
            .filter((a) => a.email?.trim())
            .map((a) => ({ email: a.email!.trim(), name: a.name })),
          createdBy: opts?.createdBy ?? opts?.recruiterName ?? null,
        });
      } catch (emailErr) {
        console.warn('[interviewEventService] Could not enqueue confirmation email:', emailErr);
      }
    }

    return event;
  },

  async update(id: string, patch: InterviewEventUpdatePatch): Promise<void> {
    const body = toDbUpdateBody(patch);
    if (Object.keys(body).length === 0) return;
    const { error } = await supabase.from('ggj_b2b_interview_events').update(body).eq('id', id);
    // Swallow outbox trigger errors on status updates — the status change still applies in most cases
    if (error && !isUndefinedTableError(error)) throw error;
    if (error) console.warn('[interviewEventService.update] Outbox trigger missing:', error.message);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_interview_events').delete().eq('id', id);
    if (error) throw error;
  },
};
